/**
 * store module used for the pricing detail
 * view in the admin area
 * @returns {{selectedDefinitions: [], selectedDate: Date}}
 */
import _ from 'lodash'
import DailyBaseRateService from '@/classes-internal/dailyBaseRate/DailyBaseRateService'
import MultiDayDiscountService from '@/classes-internal/multiDayDiscount/MultiDayDiscountService'
import MaximumEarlyBookingDiscountService from '@/classes-internal/maxEarlyBookingDiscount/MaximumEarlyBookingDiscountService'
import CashDeskPriceService from '@/classes-internal/cashDeskPrice/CashDeskPriceService'

const getDefaultState = () => ({
  selectedDate: new Date(),
  selectedDefinitions: [],
  dailyBaseRates: [],
  dailyBaseRateOverwriteMetadata: {},
  multiDayDiscounts: [],
  cashDeskPrices: [],
  cashDeskPriceOverwriteMetadata: {},
  multiDayDiscountOverwriteMetadata: {},
  maximumEarlyBookingDiscounts: [],
  maximumEarlyBookingDiscountOverwriteMetadata: {},
  dailyBaseRateService: new DailyBaseRateService(),
  multiDayDiscountService: new MultiDayDiscountService(),
  maximumEarlyBookingDiscountService: new MaximumEarlyBookingDiscountService(),
  cashDeskPriceService: new CashDeskPriceService(),
})

const state = getDefaultState()

// getters
const getters = {
  selectedDefinitions: (state) => {
    return state.selectedDefinitions
  },
  selectedDate: (state) => {
    return state.selectedDate
  },
  dailyBaseRates: (state) => {
    return state.dailyBaseRates
  },
  multiDayDiscounts: (state) => {
    return state.multiDayDiscounts
  },
  maximumEarlyBookingDiscounts: (state) => {
    return state.maximumEarlyBookingDiscounts
  },
  cashDeskPrices: (state) => {
    return state.cashDeskPrices
  },
}

// actions
const actions = {
  async fetchDailyBaseRates({ commit, state }, { from, to }) {
    const productIds = state.selectedDefinitions.map(
      (definition) => definition.item.productId
    )
    if (productIds.length === 0) {
      return
    }
    const baseRates = await state.dailyBaseRateService.fetchBetween(
      from,
      to,
      productIds
    )
    commit('setDailyBaseRates', baseRates)
  },
  async fetchMultiDayDiscounts({ commit, state }, { from, to, productDefIds }) {
    const discounts = await state.multiDayDiscountService.fetchBetween(
      from,
      to,
      productDefIds
    )
    commit('setMultiDayDiscounts', discounts)
  },
  async fetchMaximumEarlyBookingDiscounts(
    { commit, state },
    { from, to, productDefIds }
  ) {
    const earlyBookings = await state.maximumEarlyBookingDiscountService.fetchBetween(
      from,
      to,
      productDefIds
    )
    commit('setMaximumEarlyBookingDiscounts', earlyBookings)
  },
  async fetchCashDeskPrices({ commit, state }, { from, to, productDefIds }) {
    const deskPrices = await state.cashDeskPriceService.fetchBetween(
      from,
      to,
      productDefIds
    )
    commit('setCashDeskPrices', deskPrices)
  },
  async updateCashDeskPriceOverwriteMetadata(
    { commit, getters },
    chartJsEvent
  ) {
    const dataPointIndex = chartJsEvent[0]._index
    const meta = {
      max: _.maxBy(getters.cashDeskPrices, function (o) {
        return o.price
      })?.price,
      min: getters.cashDeskPrices[dataPointIndex].price,
      cashDeskPriceObj: getters.cashDeskPrices[dataPointIndex],
      initialSliderValue: Math.round(
        _.meanBy(getters.cashDeskPrices, function (o) {
          return o.price
        })
      ),
    }
    commit('setCashDeskPriceOverwriteMetadata', meta)
  },
  async updateMultiDayDiscountOverwriteMetadata(
    { commit, getters },
    chartJsEvent
  ) {
    const dataPointIndex = chartJsEvent[0]._index
    const prodDef = getters.selectedDefinitions[0].item
    // successive discounts based on the duration of the ticket 6 day = 6 entries
    const successors = []
    for (
      let i = dataPointIndex;
      i < dataPointIndex + prodDef.getDurationDays();
      i++
    ) {
      if (i < getters.multiDayDiscounts.length) {
        successors.push(getters.multiDayDiscounts[i])
      }
    }
    const meta = {
      prodDef: prodDef,
      multiDayDiscount: getters.multiDayDiscounts[dataPointIndex],
      successiveMultiDayDiscounts: successors,
    }
    commit('setMultiDayDiscountOverwriteMetadata', meta)
  },
  async updateEarlyBookingOerwriteMetadata({ commit, getters }, chartJsEvent) {
    const dataPointIndex = chartJsEvent[0]._index
    const prodDef = getters.selectedDefinitions[0].item
    // successive discounts based on the duration of the ticket 6 day = 6 entries
    const successors = []
    for (
      let i = dataPointIndex;
      i < dataPointIndex + prodDef.getDurationDays();
      i++
    ) {
      if (i < getters.maximumEarlyBookingDiscounts.length) {
        successors.push(getters.maximumEarlyBookingDiscounts[i])
      }
    }

    const meta = {
      prodDef: prodDef,
      earlyBooking: getters.maximumEarlyBookingDiscounts[dataPointIndex],
      successiveEarlyBookingDiscounts: successors,
    }
    commit('setMaximumEarlyBookingDiscountOverwriteMetadata', meta)
  },
  async updateDailyBaseRateOverwriteMetadata(
    { commit, getters },
    chartJsEvent
  ) {
    const dataPointIndex = chartJsEvent[0]._index
    const meta = {
      dailyBaseRate: getters.dailyBaseRates[dataPointIndex],
    }
    commit('setDailyBaseRateOverwriteMetadata', meta)
  },
}

// mutations
const mutations = {
  setSelectedDefinitions(state, selectedProductDefinitions) {
    state.selectedDefinitions = selectedProductDefinitions
  },
  addSelectedDefinition(state, selectedProductDefinitions) {
    state.selectedDefinitions.push(selectedProductDefinitions)
  },
  removeSelectedDefinition(state, index) {
    state.selectedDefinitions.splice(index, 1)
  },
  setSelectedDate(state, date) {
    state.selectedDate = date
  },
  setDailyBaseRates(state, baseRates) {
    state.dailyBaseRates = baseRates
  },
  setMultiDayDiscounts(state, discounts) {
    state.multiDayDiscounts = discounts
  },
  setMaximumEarlyBookingDiscounts(state, discounts) {
    state.maximumEarlyBookingDiscounts = discounts
  },
  setCashDeskPrices(state, deskPrices) {
    state.cashDeskPrices = deskPrices
  },
  setCashDeskPriceOverwriteMetadata(state, meta) {
    state.cashDeskPriceOverwriteMetadata = meta
  },
  reset(state) {
    Object.assign(state, getDefaultState())
  },
  setMultiDayDiscountOverwriteMetadata(state, meta) {
    state.multiDayDiscountOverwriteMetadata = meta
  },
  setMaximumEarlyBookingDiscountOverwriteMetadata(state, meta) {
    state.maximumEarlyBookingDiscountOverwriteMetadata = meta
  },
  setDailyBaseRateOverwriteMetadata(state, meta) {
    state.dailyBaseRateOverwriteMetadata = meta
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
